import { type InfoCardProps, ItemListPosition, WithSkeleton } from '@capsicum/common';
import { isValidElement, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';

import { Avatar, Divider, type FontVariants, InfoTooltip, SkeletonItem, Text } from '../../atoms';
import { type TooltipContentPosition } from '../../atoms/tooltip/Tooltip.type';
import { CopyToClipboard } from '../copy-to-clipboard/CopyToClipboard';
import { Timer } from '../timer/Timer';

interface ExtendedInfoCardProps extends InfoCardProps {
	tooltipPosition?: TooltipContentPosition;
}

/**
 * Price Info Component
 *
 * @param props.testID  The test id
 * @param props.title  The title of the Card
 * @param props.icon  The right icon displayed, can be string or JSX.Element, if undefined the icon is hided
 * @param props.mainRightText  The main right text
 * @param props.secondaryRightText  The secondary right text, if undefined the text is hided
 * @param props.isMainRightTextCopyable  Enable to copy the main text
 * @param props.isSecondaryRightTextCopyable  Enable to copy the secondary text
 * * @param props.secondaryRightTextValue  The real value of secondary right text to be copied (FE if we display 0xb206fe...eb6b9695, and the real value is 0xb206fec8cc11a83d8d12f879261b5bd2eb6b9695)
 * @param props.itemListPosition  The position of the Card in a list, can be SOLO, START, END, MIDDLE and it change, based on the position, the border radius of the component
 * @param props.tooltipTitle  The tooltip title displayed next the title, if tooltipText undefined the tooltip title is hided
 * @param props.tooltipText  The tooltip displayed next the title, if undefined the tooltip is hided
 * @param props.isTitleBold  Set title in bold
 * @param props.mainRightTitleSize The size of the main right text, can be SMALL REGULAR or LARGE
 * @param props.showDivider Show divider at the bottom of the element for list purpose
 * @param props.tooltipPosition The position of the tooltip if there is one
 * @param props.isRightTextStrikethrough Stoke the right text
 */
export const InfoCardSkeleton = ({ mainComponentProps }: { mainComponentProps: ExtendedInfoCardProps }) => {
	return (
		<S.Wrapper>
			<S.Container $itemListPosition={mainComponentProps.itemListPosition || ItemListPosition.SOLO}>
				<S.LabelAndTooltipWrapper>
					<Text variant={mainComponentProps.isTitleBold ? 'text1' : 'text3'}>{mainComponentProps.title}</Text>
				</S.LabelAndTooltipWrapper>
				<S.RightWrapper>
					<SkeletonItem height={25} width={100} />
				</S.RightWrapper>
			</S.Container>
			{mainComponentProps.showDivider && <S.ShortDivider orientation="horizontal" />}
		</S.Wrapper>
	);
};

export const InfoCardComponent = ({
	condensed,
	Description,
	hasRibbon,
	icon,
	iconAtRight = false,
	IconLeft,
	isMainRightTextCopyable = false,
	isRightTextStrikethrough = false,
	isSecondaryRightTextCopyable = false,
	isTitleBold = false,
	itemListPosition = ItemListPosition.SOLO,
	leftRibbonText,
	mainRightText,
	mainRightTitleSize = 'small',
	mainTitleSize = 'regular',
	rightRibbonText,
	secondaryRightText,
	secondaryRightTextValue,
	showDivider = false,
	testID,
	title: titleProp,
	tooltipPosition = 'right',
	tooltipText,
	tooltipTitle,
	withRibbonTimer = true,
	...props
}: ExtendedInfoCardProps) => {
	const mainRightTitleSizeVariant = useMemo(
		() =>
			match(mainRightTitleSize)
				.with('large', () => 'textTopBar')
				.with('regular', () => 'text3')
				.otherwise(() => 'text1') as FontVariants,
		[mainRightTitleSize]
	);

	const mainTitleSizeVariant = useMemo(
		() =>
			match(mainTitleSize)
				.with('small', () => (isTitleBold ? 'text4' : 'text5'))
				.otherwise(() => (isTitleBold ? 'text1' : 'text3')) as FontVariants,
		[mainTitleSize, isTitleBold]
	);

	const mainRightIcon = useMemo(() => {
		return icon ? (
			typeof icon === 'string' ? (
				<Avatar size="XXXS" src={icon} />
			) : isValidElement(icon) ? (
				<div>{icon}</div>
			) : null
		) : null;
	}, [icon]);

	const title = useMemo(
		() =>
			typeof titleProp === 'string' ? (
				<Text testID={`${testID}-title`} variant={mainTitleSizeVariant}>
					{titleProp}
				</Text>
			) : (
				titleProp
			),
		[testID, titleProp, mainTitleSizeVariant]
	);

	return (
		<S.Wrapper data-testid={testID}>
			<S.Container $condensed={condensed} $hasRibbon={hasRibbon} $itemListPosition={itemListPosition} {...props}>
				<S.LabelAndTooltipWrapper>
					{IconLeft && <IconLeft />}
					{title}
					{tooltipText && (
						<InfoTooltip contentPosition={tooltipPosition} testID={`${testID}-tooltip`} zIndex={12}>
							{tooltipTitle && <Text variant="title6">{tooltipTitle}</Text>}
							<S.TooltipTextWrapper>{tooltipText}</S.TooltipTextWrapper>
						</InfoTooltip>
					)}
				</S.LabelAndTooltipWrapper>
				<S.RightWrapper>
					{mainRightText && (
						<S.MainTextAndIconWrapper $isCompact={mainRightTitleSize === 'regular'}>
							{!iconAtRight && mainRightIcon}
							{typeof mainRightText === 'string' ? (
								isMainRightTextCopyable ? (
									<CopyToClipboard
										label={mainRightText}
										testID={`${testID}-copyToClipboard`}
										value={mainRightText}
									/>
								) : (
									<S.RightText
										$isRightTextStrikethrough={isRightTextStrikethrough}
										color={isRightTextStrikethrough ? 'main.disabled' : 'main.subtle'}
										testID={`${testID}-mainRightText`}
										variant={mainRightTitleSizeVariant}
									>
										{mainRightText}
									</S.RightText>
								)
							) : (
								mainRightText
							)}
							{iconAtRight && mainRightIcon}
						</S.MainTextAndIconWrapper>
					)}
					{secondaryRightText &&
						(isSecondaryRightTextCopyable ? (
							<CopyToClipboard
								label={secondaryRightText}
								testID={`${testID}-copyToClipboard`}
								value={secondaryRightTextValue || secondaryRightText}
							/>
						) : (
							<S.RightText
								$isRightTextStrikethrough={isRightTextStrikethrough}
								color={isRightTextStrikethrough ? 'main.disabled' : 'main.subtle'}
								data-testid={`${testID}-secondaryRightText`}
								variant="text2"
							>
								{secondaryRightText}
							</S.RightText>
						))}
				</S.RightWrapper>
			</S.Container>
			{Description && <Description />}
			{showDivider && <S.ShortDivider orientation="horizontal" />}
			{hasRibbon && (
				<S.FeeCoverage>
					<Text color="main.enabled" variant="caption1">
						{leftRibbonText}
					</Text>
					{withRibbonTimer && (
						<Timer customText={rightRibbonText} expirationDate={undefined} size="condensed" />
					)}
				</S.FeeCoverage>
			)}
		</S.Wrapper>
	);
};

export const InfoCard = WithSkeleton(InfoCardComponent, InfoCardSkeleton);

namespace S {
	export const Container = styled.div<{
		$condensed?: boolean;
		$hasRibbon?: boolean;
		$itemListPosition: ItemListPosition;
	}>`
		width: 100%;
		box-sizing: border-box;
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: start;

		padding: ${({ theme }) => theme.sizing.xsmall};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};

		${({ $condensed, $hasRibbon, $itemListPosition, theme }) =>
			match($itemListPosition)
				.with(
					ItemListPosition.END,
					() => css`
						border-bottom-left-radius: ${theme.sizing.xxxsmall};
						border-bottom-right-radius: ${theme.sizing.xxxsmall};
						padding-top: ${$condensed ? theme.sizing.xxxsmall : theme.sizing.xsmall};
					`
				)
				.with(
					ItemListPosition.START,
					() => css`
						border-top-left-radius: ${theme.sizing.xxxsmall};
						border-top-right-radius: ${theme.sizing.xxxsmall};
						padding-bottom: ${$condensed ? theme.sizing.xxxsmall : theme.sizing.xsmall};
					`
				)
				.with(
					ItemListPosition.SOLO,
					() => css`
						border-radius: ${theme.sizing.xxxsmall};
						border-bottom-left-radius: ${$hasRibbon ? 0 : theme.sizing.xxxsmall};
						border-bottom-right-radius: ${$hasRibbon ? 0 : theme.sizing.xxxsmall};
					`
				)
				.otherwise(() => css``)};
	`;
	export const LabelAndTooltipWrapper = styled.div`
		display: flex;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		align-items: center;
	`;
	export const RightWrapper = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;
	export const MainTextAndIconWrapper = styled.div<{ $isCompact: boolean }>`
		display: flex;
		flex-direction: row;
		gap: ${({ $isCompact, theme }) => ($isCompact ? theme.spacings.xxxsmall : theme.spacings.xxsmall)};
		align-items: center;
		justify-content: right;
	`;
	export const RightText = styled(Text)<{ $isRightTextStrikethrough: boolean }>`
		text-align: right;
		text-decoration: ${({ $isRightTextStrikethrough }) => $isRightTextStrikethrough && 'line-through'};
	`;
	export const ShortDivider = styled(Divider)`
		position: absolute;
		bottom: 0;
		left: 50%;
		transform: translate(-50%, 0);
		width: calc(100% - ${({ theme }) => `${theme.spacings.xsmall} * 2`});
	`;
	export const Wrapper = styled.div`
		position: relative;
	`;

	export const TooltipTextWrapper = styled.div`
		white-space: pre-wrap;
	`;

	export const FeeCoverage = styled.div`
		display: flex;
		align-items: center;
		justify-content: space-between;
		background-color: ${({ theme }) => theme.colors.backgrounds.info.subtle};
		padding: ${({ theme }) => `${theme.spacings.xxxsmall} ${theme.spacings.small} `};
		border-bottom-left-radius: ${({ theme }) => theme.sizing.xxxsmall};
		border-bottom-right-radius: ${({ theme }) => theme.sizing.xxxsmall};
	`;
}
